<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import { ApplicantType } from '@/helpers/const';
import { getFullName } from '@/helpers/format';
import { useCustomerQuoteProcess } from '@/helpers/useCustomerQuoteProcess';
import { TrashIcon } from '@heroicons/vue/24/outline';

import { applicantSchema } from '@/validation';
import { applicantSimpleSchema } from '@/validation/schemas/simple/applicant';

import ApplicantForm from './ApplicantForm.vue';

import type { ApplicantEntry, ApplicantTypeEnum } from '@/types/Applicant';

import { useRelationshipManager } from '@/helpers/useRelationshipManager';

const props = withDefaults(
  defineProps<{
    modelValue: ApplicantEntry;
    type: ApplicantTypeEnum;
    primary?: ApplicantEntry;
    allApplicants?: ApplicantEntry[];
    currentId?: string;
  }>(),
  {
    primary: undefined,
    allApplicants: () => [],
    currentId: '',
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', v: ApplicantEntry): void;
  (e: 'edit', id: string): void;
  (e: 'remove', id: string): void;
  (e: 'relationship-change', applicantId: string, newStatus: string, oldStatus?: string): void;
}>();

const { sectionStates } = useCustomerQuoteProcess();

const showEntry = ref(!props.modelValue.firstName || !props.modelValue.lastName);
const applicantRef = ref<InstanceType<typeof ApplicantForm>>();
const applicant = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});

const applicantDetailsType = computed(() => sectionStates.value.applicantDetails);
const fullName = computed(() => getFullName(props.modelValue));
const isApplicantPrimary = computed(() => props.type === ApplicantType.PRIMARY);

const applicantsRef = ref<ApplicantEntry[]>(props.allApplicants || []);
const { getRelationshipOptions, validate } = useRelationshipManager(applicantsRef);
const relationshipOptions = computed(() => getRelationshipOptions(props.modelValue.id));

const relationshipErrors = computed(() => {
  const allErrors = validate.value;

  return allErrors.filter((error) => {
    if (error.includes(`[${props.modelValue.id}]`)) {
      return true;
    }

    return false;
  });
});

const saveChanges = async () => {
  const saved = (await applicantRef.value?.update()) ?? false;
  if (saved) {
    showEntry.value = false;
  }
  return saved;
};

const editItem = () => {
  showEntry.value = true;
  applicantRef.value?.resetForm({ values: { ...props.modelValue, type: props.type } });
};

const cancelChanges = () => {
  applicantRef.value?.cancel();
  showEntry.value = false;
};

const showModal = ref(false);
const openModal = () => {
  showModal.value = true;
};
const removeItem = () => {
  showModal.value = false;
  emit('remove', props.modelValue.id);
};

defineExpose({ update: saveChanges });

watch(
  () => props.allApplicants,
  (newApplicants) => {
    applicantsRef.value = newApplicants || [];
  },
  { immediate: true, deep: true },
);

watch(
  () => props.modelValue,
  async (val) => {
    if (!val.firstName && !val.lastName) {
      const currentSchema =
        applicantDetailsType.value === 'simple' ? applicantSimpleSchema() : applicantSchema(props.primary);
      showEntry.value = !(await currentSchema.isValid(val));
    }
  },
  { immediate: true },
);
</script>

<template>
  <div :class="{ 'mb-5': props.type === ApplicantType.SECONDARY }">
    <BxExpander v-model="showEntry" separator overflow-visible>
      <template #header>
        <div class="px-4 py-5 sm:px-6">
          <template v-if="!modelValue.firstName">
            <div class="flex flex-wrap items-center justify-between sm:flex-nowrap">
              <h3 class="flex items-center text-lg font-medium leading-6 text-gray-900">
                <span class="mr-3 hidden md:block"><BxIcon name="customer" /></span>
                <span>{{ isApplicantPrimary ? 'Primary applicant' : 'New applicant' }} details</span>
              </h3>
            </div>
          </template>
          <template v-else-if="showEntry">
            <h3 class="flex items-center text-lg font-medium leading-6 text-gray-900">
              <span class="mr-3 hidden md:block"><BxIcon name="customer" /></span>
              <span>{{ fullName }}</span>
            </h3>
          </template>
          <template v-else>
            <div class="-ml-4 -mt-4 flex flex-wrap items-end justify-between sm:flex-nowrap">
              <div class="w-100 ml-4 mt-4">
                <h3 class="flex items-center text-lg font-medium leading-6 text-gray-900">
                  <span class="mr-3 hidden md:block"><BxIcon name="customer" /></span>
                  <span>{{ fullName }}</span>
                </h3>
                <div class="w-100 max-w-500">
                  <BxSelect
                    :id="modelValue.id + '-relationship-status-card'"
                    class="form-control !mt-10"
                    :error-message="relationshipErrors.map((error) => error.replace(/^\[[^\]]+\]\s*/, '')).join('. ')"
                    :model-value="modelValue.relationshipStatus"
                    :list-items="relationshipOptions"
                    label="Relationship"
                    label-type="floating"
                    @update:model-value="(value: string | Record<string, unknown>) => {
                        const oldStatus = modelValue.relationshipStatus;
                        const stringValue = typeof value === 'string' ? value : (value as any).id || '';
                        emit('update:modelValue', { ...modelValue, relationshipStatus: stringValue as any });
                        emit('relationship-change', modelValue.id, stringValue, oldStatus);
                      }"
                  />
                </div>
              </div>
              <div class="mb-3 ml-4 mt-4 flex-shrink-0">
                <BxButton variant="secondary" @click="editItem()">Edit</BxButton>
                <BxButton
                  variant="link"
                  class="ml-4"
                  :disabled="props.type !== ApplicantType.SECONDARY"
                  @click="openModal()"
                >
                  Remove
                </BxButton>
              </div>
            </div>
          </template>
        </div>
      </template>
      <template #body>
        <div class="p-6">
          <ApplicantForm
            ref="applicantRef"
            v-model="applicant"
            :type="props.type"
            :primary="primary"
            :all-applicants="props.allApplicants"
            :current-id="props.currentId"
            @relationship-change="(applicantId: string, newStatus: string, oldStatus?: string) => emit('relationship-change', applicantId, newStatus, oldStatus)"
          />
          <div class="mt-10 flex-shrink-0">
            <BxButton :id="`${applicant.id}-applicant-save`" variant="secondary" @click="saveChanges()">Save</BxButton>
            <BxButton v-if="modelValue.firstName" variant="link" class="ml-4" @click="cancelChanges()">Cancel</BxButton>
            <BxButton v-else variant="link" class="ml-4" @click="openModal()">Cancel</BxButton>
          </div>
        </div>
      </template>
    </BxExpander>
    <BxModal
      :open="showModal"
      :icon="TrashIcon"
      title="Remove applicant"
      confirm-label="Remove"
      cancel-label="Cancel"
      @close="showModal = false"
      @confirm="removeItem()"
    >
      Do you want to remove
      <span class="text-bridgit-green">{{ fullName || 'the new applicant' }}</span
      >?
    </BxModal>
  </div>
</template>

<style scoped>
.w-100 {
  width: 100%;
}

.max-w-500 {
  max-width: 500px;
}
</style>
